import React, {ComponentPropsWithRef, useCallback, useEffect} from 'react'

import {type UseEmblaCarouselType} from 'embla-carousel-react'
import styled from 'styled-components'
import {styledTheme} from '@festi/common/themes'
import {media} from 'styled-bootstrap-grid'
import classNames from 'classnames'

const Button = styled.button`
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  padding: 0;
  border: 1px solid ${styledTheme.palette.ui10Solid};
  border-radius: 12px;
  transition: border-color 0.15s;
  outline: none !important;
  background-color: transparent;
  cursor: pointer;

  &:focus-visible {
    border: 2px solid ${styledTheme.palette.lightBlue};
  }

  &:focus {
    outline: none !important;
  }

  &.selected {
    border: 2px solid ${styledTheme.palette.lightBlue};
  }

  ${media.md`
    width: 80px;
    height: 80px;
    flex: 0 0 80px;
  `}
`

type CarouselApi = UseEmblaCarouselType[1]
type UseThumbButtonType = {
  onThumbButtonClick: (index: number) => void
}

export const useThumbButton = (
  emblaMainApi: CarouselApi | undefined,
  emblaThumbsApi: CarouselApi | undefined,
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>,
): UseThumbButtonType => {
  const onThumbButtonClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return
      emblaMainApi.scrollTo(index)
    },
    [emblaMainApi, emblaThumbsApi],
  )

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return
    setSelectedIndex(emblaMainApi.selectedScrollSnap())
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap())

    const slides = emblaMainApi.slideNodes()

    emblaMainApi.slidesInView().forEach((indexInView) => {
      slides[indexInView].removeAttribute('aria-hidden')
    })
    emblaMainApi.slidesNotInView().forEach((indexNotInView) => {
      slides[indexNotInView].setAttribute('aria-hidden', 'true')
    })
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaMainApi) return
    onSelect()

    emblaMainApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaMainApi, onSelect])

  return {
    onThumbButtonClick,
  }
}

type PropType = ComponentPropsWithRef<'button'> & {
  selected: boolean
}

export const ThumbButton: React.FC<PropType> = (props) => {
  const {selected, children, ...restProps} = props

  return (
    <Button className={classNames({selected})} type="button" {...restProps}>
      {children}
    </Button>
  )
}
